import { scrollTopTo } from '@/shared';
import classnames from 'classnames';
import {
  useContext,
  createContext,
  useReducer,
  useEffect,
  ReactNode,
} from 'react';
import './index.less';

export interface AnchorInfo {
  id: string;
  label: string;
  isLink: boolean;
  link?: string;
}

interface NavAnchorContextState {
  anchors: AnchorInfo[];
}
export interface NavAnchorContextData {
  state: NavAnchorContextState;
  dispatch: React.Dispatch<{
    type: string;
    payload: AnchorInfo;
  }>;
}

export const NavAnchorContext = createContext<NavAnchorContextData>({
  state: { anchors: [] },
  dispatch: () => void 0,
});

function navAnchorReducer(
  state: NavAnchorContextState,
  action: { type: string; payload: AnchorInfo },
) {
  switch (action.type) {
    case 'update': {
      const { anchors } = state;
      return {
        anchors: [
          ...anchors.filter((item) => item.id !== action.payload.id),
          action.payload,
        ],
      };
    }
    case 'remove': {
      const { anchors } = state;
      return {
        anchors: [...anchors.filter((item) => item.id !== action.payload.id)],
      };
    }
    case 'clear': {
      return { anchors: [] };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

export function NavAnchorProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(navAnchorReducer, { anchors: [] });
  // NOTE: you *might* need to memoize this value
  // Learn more in http://kcd.im/optimize-context
  const value = { state, dispatch };
  return (
    <NavAnchorContext.Provider value={value}>
      {children}
    </NavAnchorContext.Provider>
  );
}

/**
 * 锚点导航链接元素
 */
function NavAnchorLink(props: React.HTMLAttributes<HTMLAnchorElement>) {
  const { children, ...otherProps } = props;
  return <span {...otherProps}>{children}</span>;
}

/**
 * 自动收集页面上的 AnchorContent 生成导航
 */
export function NavAnchor(props: React.HTMLAttributes<HTMLUListElement>) {
  const { className, ...otherProps } = props;
  const { state } = useContext(NavAnchorContext);
  const handleClick = (targetId: string) => {
    try {
      const el = document.getElementById(targetId);
      if (el !== null) {
        const targetTop = window.pageXOffset + el.getBoundingClientRect().top;
        scrollTopTo(targetTop);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`Failed to get element by id ${targetId}`);
    }
  };
  return (
    <ul className={classnames('mag-nav-anchor', className)} {...otherProps}>
      {state.anchors.map((item) => (
        <li
          key={item.id}
          onClick={
            item.isLink
              ? () => {
                  window.open(item.link);
                }
              : () => handleClick(item.id)
          }
        >
          <NavAnchorLink className="mag-nav-link">{item.label}</NavAnchorLink>
        </li>
      ))}
    </ul>
  );
}

interface NavAnchorContentProps {
  id: string;
  label: string;
  isLink: boolean;
  link?: string;
}

/**
 * 使用此组件注册的锚点会被自动收集为导航列表数据
 */
export function NavAnchorContent(
  props: NavAnchorContentProps & React.HTMLAttributes<HTMLDivElement>,
) {
  const { id, label, isLink, link, className, ...otherProps } = props;
  const { dispatch } = useContext(NavAnchorContext);

  useEffect(() => {
    const anchor = { id, label, isLink, link };
    dispatch({ type: 'update', payload: anchor });

    return () => {
      dispatch({ type: 'remove', payload: anchor });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, label]);

  return (
    <div id={id} className={className} {...otherProps}>
      {props.children}
    </div>
  );
}
